/**
 * Page /grc/dossiers-dechetterie — suivi & instruction des demandes de carte
 * d'accès à la déchetterie, au même endroit que les autres dossiers (scolaires,
 * associations, subventions, petite enfance).
 *
 * Filtres : À traiter (nouvelle) / En attente (en_cours) / Validés (acceptee) /
 * Refusés (refusee) / Tous. Instruction : accepter (numéro auto ou saisi),
 * mettre en cours, refuser (motif). La CONFIGURATION du dossier (pièces
 * demandées, formulaire, activation, numérotation) reste sur la fiche
 * déchetterie dans « Services & vie quotidienne ».
 */

import { useEffect, useState, useCallback } from 'react';
import { api, ApiException } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { GrcLayout } from '@/components/grc/GrcLayout';
import { Card, Button, Badge, Alert, Field, Input, LogoMark, SectionEyebrow } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { Recycle, AlertCircle, Inbox, Check, X, User, FileText, Trash2 } from 'lucide-react';
import type { CollecteDossier, CollecteDossierStatut } from '@/lib/api';

const BUCKETS: { key: CollecteDossierStatut | 'tous'; label: string }[] = [
  { key: 'nouvelle', label: 'À traiter' },
  { key: 'en_cours', label: 'En attente' },
  { key: 'acceptee', label: 'Validés' },
  { key: 'refusee', label: 'Refusés' },
  { key: 'tous', label: 'Tous' },
];

function frDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
}

function statutBadge(s: CollecteDossierStatut) {
  switch (s) {
    case 'acceptee': return <Badge variant="success" size="sm">Validé</Badge>;
    case 'refusee': return <Badge variant="urgent" size="sm">Refusé</Badge>;
    case 'en_cours': return <Badge variant="info" size="sm">En cours</Badge>;
    default: return <Badge variant="warning" size="sm">Nouvelle</Badge>;
  }
}

export function GrcDossiersDechetteriePage() {
  const { user } = useAuth();
  const peutInstruire = user ? ['super_admin', 'admin', 'manager'].includes(user.role) : false;
  const { confirm, dialogElement } = useConfirmDialog();

  const [items, setItems] = useState<CollecteDossier[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bucket, setBucket] = useState<CollecteDossierStatut | 'tous'>('nouvelle');

  const [detail, setDetail] = useState<CollecteDossier | null>(null);
  const [numeroCarte, setNumeroCarte] = useState('');
  const [reponse, setReponse] = useState('');
  const [modeDelivrance, setModeDelivrance] = useState<'dematerialisee' | 'physique' | 'les_deux'>('dematerialisee');
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const statut = bucket === 'tous' ? undefined : bucket;
      const r = await api.grc.collecte.listDossiers(statut);
      setItems(r.dossiers);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Chargement impossible.');
      setItems([]);
    } finally { setLoading(false); }
  }, [bucket]);

  useEffect(() => { void reload(); }, [reload]);

  async function statuer(d: CollecteDossier, statut: CollecteDossierStatut) {
    setBusy(true); setError(null);
    try {
      await api.grc.collecte.changerStatutDossier(d.id, {
        statut,
        numeroCarte: statut === 'acceptee' ? (numeroCarte.trim() || null) : null,
        reponseStaff: statut === 'refusee' ? (reponse.trim() || null) : null,
        modeDelivrance: statut === 'acceptee' ? modeDelivrance : null,
      });
      setDetail(null); setNumeroCarte(''); setReponse('');
      await reload();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Action impossible.');
    } finally { setBusy(false); }
  }

  async function supprimer(d: CollecteDossier) {
    const ok = await confirm({
      title: 'Supprimer ce dossier ?',
      description: 'Les pièces jointes seront également supprimées.',
      confirmLabel: 'Supprimer', danger: true,
    });
    if (!ok) return;
    try { await api.grc.collecte.removeDossier(d.id); setDetail(null); await reload(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur.'); }
  }

  return (
    <GrcLayout active="dossiers">
      <main id="main-content" className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-10 animate-fade-in">
        <div className="mb-6 flex items-center gap-3">
          <LogoMark icon={Recycle} accent="grc" size="lg" />
          <div>
            <SectionEyebrow as="div">Gestion des dossiers déchetterie</SectionEyebrow>
            <h1 className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl">Cartes d'accès déchetterie</h1>
          </div>
        </div>

        {error && <Alert variant="error" icon={AlertCircle} className="mb-3">{error}</Alert>}

        <div className="mb-4 flex flex-wrap gap-1.5">
          {BUCKETS.map((b) => (
            <button key={b.key} type="button" onClick={() => setBucket(b.key)}
              className={['inline-flex h-9 items-center rounded-r-pill px-4 text-sm font-medium transition-all',
                bucket === b.key ? 'bg-grc text-white shadow-soft' : 'text-ink-muted hover:bg-surface/70'].join(' ')}>
              {b.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-sm text-ink-muted">Chargement…</p>
        ) : !items || items.length === 0 ? (
          <Card>
            <div className="flex flex-col items-center gap-3 p-8 text-center">
              <Inbox className="h-8 w-8 text-ink-subtle" />
              <div>
                <p className="text-sm font-medium text-ink">Aucun dossier dans cette vue.</p>
                <p className="mt-1 text-sm text-ink-muted">
                  Les habitants déposent leur demande depuis « Infos, alertes &amp; services ».
                  La configuration (pièces demandées, numérotation) se règle sur la fiche
                  déchetterie dans <strong>Services &amp; vie quotidienne</strong>.
                </p>
              </div>
            </div>
          </Card>
        ) : (
          <ul className="space-y-2">
            {items.map((d) => (
              <li key={d.id}>
                <button type="button" onClick={() => { setDetail(d); setNumeroCarte(d.numeroCarte ?? ''); setReponse(d.reponseStaff ?? ''); setModeDelivrance(d.modeDelivrance ?? 'dematerialisee'); }} className="w-full text-left">
                  <Card>
                    <div className="flex items-center justify-between gap-3 p-4 transition-colors hover:bg-surface/40">
                      <div className="flex items-center gap-2">
                        <User className="h-5 w-5 text-grc-fg" aria-hidden="true" />
                        <div>
                          <div className="text-sm font-medium text-ink">{d.demandeurNom ?? 'Administré'}</div>
                          <div className="text-xs text-ink-subtle">
                            {d.pieces.length} pièce(s) · déposé le {frDate(d.createdAt)}
                            {d.numeroCarte ? ` · ${d.numeroCarte}` : ''}
                          </div>
                        </div>
                      </div>
                      {statutBadge(d.statut)}
                    </div>
                  </Card>
                </button>
              </li>
            ))}
          </ul>
        )}
      </main>

      {/* Dialog instruction */}
      <Dialog
        open={!!detail} onClose={() => setDetail(null)}
        title={detail ? `Dossier — ${detail.demandeurNom ?? 'Administré'}` : ''}
        footer={detail && (
          <Button variant="ghost" onClick={() => setDetail(null)}>Fermer</Button>
        )}
      >
        {detail && (
          <div className="space-y-3">
            <div className="rounded-r-md border border-border bg-bg p-3 text-sm">
              {detail.demandeurEmail && <div className="text-ink-muted">{detail.demandeurEmail}</div>}
              {detail.demandeurTel && <div className="text-ink-muted">{detail.demandeurTel}</div>}
              {detail.demandeurAdresse && <div className="text-ink-muted">{detail.demandeurAdresse}</div>}
              {detail.message && <div className="mt-1 text-ink">{detail.message}</div>}
              <div className="mt-1 text-xs text-ink-dim">Déposé le {frDate(detail.createdAt)}</div>
            </div>

            {detail.pieces.length > 0 && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-ink-dim">Pièces déposées</div>
                <ul className="mt-1.5 flex flex-wrap gap-2">
                  {detail.pieces.map((pj) => (
                    <li key={pj.id}>
                      {pj.url
                        ? <a href={pj.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-r-pill border border-border bg-bg-2 px-2 py-0.5 text-xs text-ink-muted hover:text-ink"><FileText className="h-3 w-3" />{pj.libelle ?? pj.nomFichier ?? 'pièce'}</a>
                        : <span className="text-xs text-ink-dim">{pj.libelle ?? pj.nomFichier}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {peutInstruire ? (
              <>
                <Field label="Mode de délivrance de la carte" htmlFor="dd-mode">
                  <select id="dd-mode" value={modeDelivrance}
                    onChange={(e) => setModeDelivrance(e.target.value as typeof modeDelivrance)}
                    className="w-full rounded-r-md border border-border bg-bg px-3 py-2 text-sm text-ink">
                    <option value="dematerialisee">Dématérialisée (attestation PDF)</option>
                    <option value="physique">Physique (carte remise en mairie)</option>
                    <option value="les_deux">Les deux</option>
                  </select>
                </Field>
                <Field label="Numéro de carte (laisser vide = généré automatiquement)" htmlFor="dd-num">
                  <Input id="dd-num" value={numeroCarte} onChange={(e) => setNumeroCarte(e.target.value)} placeholder="Auto (ex. DECH-2026-0001)" />
                </Field>
                <Field label="Motif (si refusé)" htmlFor="dd-rep">
                  <Input id="dd-rep" value={reponse} onChange={(e) => setReponse(e.target.value)} placeholder="Justificatif de domicile manquant" />
                </Field>
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button variant="ghost" size="sm" disabled={busy} onClick={() => statuer(detail, 'en_cours')}>Marquer en cours</Button>
                  <Button variant="primary" size="sm" disabled={busy} onClick={() => statuer(detail, 'acceptee')}><Check className="h-4 w-4" /> Accepter</Button>
                  <Button variant="danger" size="sm" disabled={busy} onClick={() => statuer(detail, 'refusee')}><X className="h-4 w-4" /> Refuser</Button>
                  <Button variant="ghost" size="sm" disabled={busy} onClick={() => supprimer(detail)} className="ml-auto"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </>
            ) : (
              <p className="text-xs text-ink-subtle">L'instruction des dossiers est réservée à un responsable.</p>
            )}
          </div>
        )}
      </Dialog>

      {dialogElement}
    </GrcLayout>
  );
}
